<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

// Use the globally-configured axios instance which already injects the
// DeviseTokenAuth headers (access-token, client, uid, expiry) required by
// the Chatwoot API. Importing axios directly would yield a clean instance
// without those headers and the backend would respond 401.
const wootApi = window.axios;

const store = useStore();
const router = useRouter();

const inboxName = ref('');
const loading = ref(false);
const qrSource = ref('');
const state = ref('idle');
const errorMessage = ref('');
const startedAt = ref(null);
const qrExpiresIn = ref(0);
const qrRefreshing = ref(false);

let pollTimer = null;
let qrRefreshTimer = null;

const STATE_POLL_INTERVAL_MS = 4000;
const QR_REFRESH_INTERVAL_MS = 50000;
const QR_REFRESH_LEAD_SECONDS = 2;

const resetQrCountdown = () => {
  qrExpiresIn.value = Math.ceil(QR_REFRESH_INTERVAL_MS / 1000);
};

const accountId = computed(() => store.getters.getCurrentAccountId);
const basePath = computed(
  () => `/api/v1/accounts/${accountId.value}/integrations/evolution`
);

const slugify = value =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Auto-derive instance name from inbox name so the user never has to fill it in.
// The backend further scopes it with acc{id}- to avoid cross-account conflicts.
const normalizedInstance = computed(() => {
  const base = slugify(inboxName.value || 'wpp');
  return base || 'wpp';
});

const qrImageSrc = computed(() => {
  if (!qrSource.value) return '';
  if (qrSource.value.startsWith('data:')) return qrSource.value;
  return `data:image/png;base64,${qrSource.value}`;
});

const stopTimers = () => {
  if (pollTimer) clearInterval(pollTimer);
  if (qrRefreshTimer) clearInterval(qrRefreshTimer);
  pollTimer = null;
  qrRefreshTimer = null;
  qrExpiresIn.value = 0;
  qrRefreshing.value = false;
};

const fetchQr = async ({ force = false } = {}) => {
  try {
    qrRefreshing.value = force;
    const { data } = await wootApi.get(`${basePath.value}/qrcode`, {
      params: { instance_name: normalizedInstance.value, force },
    });
    const next = data?.base64 || data?.code || data?.qrcode?.base64 || '';
    if (next) {
      qrSource.value = next;
      resetQrCountdown();
    }
  } catch (e) {
    // ignore — keep last QR until next tick
  } finally {
    qrRefreshing.value = false;
  }
};

const startQrRefreshCountdown = () => {
  resetQrCountdown();
  qrRefreshTimer = setInterval(async () => {
    qrExpiresIn.value = Math.max(qrExpiresIn.value - 1, 0);
    if (qrExpiresIn.value <= QR_REFRESH_LEAD_SECONDS && !qrRefreshing.value) {
      await fetchQr({ force: true });
    }
  }, 1000);
};

const finalizeInbox = async () => {
  stopTimers();
  state.value = 'open';
  try {
    // POST /finalize — creates the Chatwoot Channel::Api inbox NOW that the
    // Evolution state has reached 'open' (i.e. the user actually scanned the
    // QR). Before this commit the inbox was created up-front during /create,
    // which left an orphan inbox in the dashboard whenever the user closed
    // the modal without scanning.
    const { data } = await wootApi.post(`${basePath.value}/finalize`, {
      instance_name: normalizedInstance.value,
      name_inbox: inboxName.value || `WhatsApp ${normalizedInstance.value}`,
    });
    if (data?.inbox_id) {
      router.replace({
        name: 'settings_inbox_finish',
        params: { inbox_id: data.inbox_id },
      });
      return;
    }
  } catch (e) {
    // This number is already paired to another inbox in this account. The
    // backend already tore down the duplicate instance — surface the message
    // and send the user to the inbox that already owns the number so they can
    // Reconnect there instead of creating a parallel inbox.
    if (e?.response?.data?.error === 'number_already_connected') {
      errorMessage.value = e.response.data.message;
      state.value = 'duplicate';
      if (e.response.data.inbox_id) {
        router.replace({
          name: 'settings_inbox_show',
          params: { inboxId: e.response.data.inbox_id },
        });
      }
      return;
    }
    // fall back to GET /inbox in case the inbox was already created in a
    // previous attempt (e.g. user reloaded the page after the QR scan).
    try {
      const { data } = await wootApi.get(`${basePath.value}/inbox`, {
        params: {
          instance_name: normalizedInstance.value,
          name_inbox: inboxName.value || `WhatsApp ${normalizedInstance.value}`,
        },
      });
      if (data?.inbox_id) {
        router.replace({
          name: 'settings_inbox_finish',
          params: { inbox_id: data.inbox_id },
        });
        return;
      }
    } catch (_) {
      // fall through to settings list
    }
  }
  router.replace({ name: 'settings_inbox_list' });
};

const pollState = async () => {
  try {
    const { data } = await wootApi.get(`${basePath.value}/state`, {
      params: { instance_name: normalizedInstance.value },
    });
    const s = data?.instance?.state || data?.state || '';
    if (s) state.value = s;
    if (s === 'open') {
      await finalizeInbox();
    }
  } catch (e) {
    // keep polling
  }
};

const createInstance = async () => {
  errorMessage.value = '';
  if (!inboxName.value.trim()) {
    errorMessage.value = 'Informe um nome para a caixa de entrada.';
    return;
  }
  loading.value = true;
  try {
    const { data } = await wootApi.post(basePath.value, {
      instance_name: normalizedInstance.value,
      name_inbox: inboxName.value || `WhatsApp ${normalizedInstance.value}`,
    });
    const qr = data?.qr || {};
    qrSource.value = qr.base64 || qr.code || '';
    state.value = 'connecting';
    startedAt.value = Date.now();
    stopTimers();

    // Always show the QR screen for a brand new inbox. The backend guarantees
    // the inbox name is unique and forces a logout/restart if the Evolution
    // instance was orphaned in `open` state, so we never want to short-circuit
    // here based on a stale state read.

    pollTimer = setInterval(pollState, STATE_POLL_INTERVAL_MS);
    startQrRefreshCountdown();
  } catch (e) {
    errorMessage.value =
      e.response?.data?.message ||
      e.response?.data?.error ||
      e.message ||
      'Erro desconhecido';
  } finally {
    loading.value = false;
  }
};

// Abort: tell the backend to wipe the orphan Evolution instance so we don't
// accumulate dead pairings (and so the next attempt with the same name gets
// a brand new QR). Safe to call even if the instance was never created.
const abortInstance = async () => {
  if (!normalizedInstance.value) return;
  try {
    await wootApi.delete(`${basePath.value}/cancel`, {
      params: { instance_name: normalizedInstance.value },
    });
  } catch (e) {
    // best-effort; leave a log but don't block the navigation
    // eslint-disable-next-line no-console
    console.warn('[Evolution] cancel failed:', e?.message || e);
  }
};

const cancel = async () => {
  stopTimers();
  const wasMidFlow = state.value !== 'idle' && state.value !== 'open';
  if (wasMidFlow) await abortInstance();
  qrSource.value = '';
  state.value = 'idle';
};

onUnmounted(() => {
  stopTimers();
  // If the user navigates away mid-flow (closes the tab, hits back, switches
  // routes) without finishing the QR scan, wipe the orphan Evolution instance.
  if (state.value !== 'idle' && state.value !== 'open') {
    abortInstance();
  }
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div class="mb-6">
      <h1 class="mb-2 text-lg font-medium text-n-slate-12">
        Evolution API (WhatsApp)
      </h1>
      <p class="text-sm leading-relaxed text-n-slate-11">
        Conecte um WhatsApp não-oficial via Evolution API. A caixa de entrada
        só é criada depois que você escaneia o QR Code com sucesso. Se
        você cancelar ou sair antes de escanear, nada é registrado.
      </p>
    </div>

    <div v-if="state === 'idle'" class="space-y-4 max-w-md">
      <label class="block">
        <span class="text-sm font-medium text-n-slate-12">
          Nome da caixa de entrada
        </span>
        <input
          v-model="inboxName"
          type="text"
          placeholder="ex: WhatsApp Principal"
          class="block px-3 py-2 mt-1 w-full text-sm rounded border bg-n-alpha-black1 border-n-weak text-n-slate-12"
        />
      </label>

      <p v-if="errorMessage" class="text-sm text-red-500">
        {{ errorMessage }}
      </p>

      <button
        :disabled="loading || !inboxName.trim()"
        class="px-4 py-2 text-sm font-medium rounded bg-n-brand text-n-solid-3 disabled:opacity-50"
        @click="createInstance"
      >
        {{ loading ? 'Criando…' : 'Criar e gerar QR Code' }}
      </button>
    </div>

    <div v-else class="space-y-4 max-w-md">
      <div class="text-sm text-n-slate-11">
        Estado da instância:
        <strong class="text-n-slate-12">{{ state }}</strong>
      </div>

      <div
        class="flex justify-center items-center w-72 h-72 bg-white rounded border border-n-weak"
      >
        <img
          v-if="qrImageSrc"
          :src="qrImageSrc"
          alt="QR Code Evolution"
          class="w-full h-full object-contain"
        />
        <span v-else class="text-sm text-n-slate-11">Aguardando QR…</span>
      </div>

      <p class="text-xs text-n-slate-11">
        Abra o WhatsApp no seu celular → Aparelhos conectados → Conectar um
        aparelho. O QR atualiza automaticamente até a conexão completar.
      </p>

      <p v-if="qrExpiresIn" class="text-xs font-medium text-n-slate-12">
        {{ qrRefreshing ? 'Renovando QR...' : `QR expira/renova em ${qrExpiresIn}s` }}
      </p>

      <p v-if="errorMessage" class="text-sm text-red-500">
        {{ errorMessage }}
      </p>

      <button
        class="px-3 py-1.5 text-xs rounded border border-n-weak text-n-slate-11"
        @click="cancel"
      >
        Cancelar
      </button>
    </div>
  </div>
</template>
